import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ProfilesCarousel — carrossel acessível com as 5 telas "Hoje".
 * Estático: imagens em /marketing/screens/*.webp (fallback .png).
 * Autoplay 6s (pausa em hover/focus, respeita prefers-reduced-motion),
 * teclado (setas), swipe, indicadores, lazy exceto slide 1.
 */
const IMG_W = 1440;
const IMG_H = 810;

const ProfilesCarousel = ({ title, subtitle, slides, ariaLabel }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const reducedMotion = useMemo(
    () => typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const go = useCallback(
    (delta) => setIndex((i) => (i + delta + slides.length) % slides.length),
    [slides.length]
  );

  useEffect(() => {
    if (paused || reducedMotion) return undefined;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [paused, reducedMotion, go]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 48) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const V = { text: '#111111', textSoft: '#555555', border: '#e6e6e6', cardBg: '#fafafa' };

  return (
    <section id="cinco-visoes" className="py-20 border-t" style={{ borderColor: V.border }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center" style={{ color: V.text }}>
          {title}
        </h2>
        <p className="mt-3 text-center text-base sm:text-lg max-w-2xl mx-auto" style={{ color: V.textSoft }}>
          {subtitle}
        </p>

        <div
          role="region"
          aria-roledescription="carousel"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="mt-10 outline-none"
        >
          <div className="relative rounded-2xl border overflow-hidden" style={{ borderColor: V.border, backgroundColor: V.cardBg }}>
            {slides.map((s, i) => (
              <div
                key={s.img}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} / ${slides.length}: ${s.title}`}
                aria-hidden={i !== index}
                style={{
                  display: i === index ? 'block' : 'none',
                }}
              >
                <picture>
                  <source srcSet={`/marketing/screens/${s.img}.webp`} type="image/webp" />
                  <img
                    src={`/marketing/screens/${s.img}.png`}
                    alt={s.alt}
                    width={IMG_W}
                    height={IMG_H}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    className="w-full h-auto block"
                  />
                </picture>
                <div className="px-5 py-4 border-t bg-white" style={{ borderColor: V.border }}>
                  <h3 className="text-base font-bold" style={{ color: V.text }}>{s.title}</h3>
                  <p className="mt-1 text-sm" style={{ color: V.textSoft }}>{s.description}</p>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border shadow-sm flex items-center justify-center hover:bg-gray-50"
              style={{ borderColor: V.border, color: V.text }}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Próximo"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border shadow-sm flex items-center justify-center hover:bg-gray-50"
              style={{ borderColor: V.border, color: V.text }}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="Slides">
            {slides.map((s, i) => (
              <button
                key={s.img}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={s.title}
                onClick={() => setIndex(i)}
                className="rounded-full transition-all"
                style={{
                  width: i === index ? 24 : 8,
                  height: 8,
                  backgroundColor: i === index ? '#111111' : '#d1d5db',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfilesCarousel;
